import { ShipFromAddress, PackageDimensions, MiscItem, Order, EasyPostLabel } from '../types';
import { 
  getShipFromAddresses as getShipFromAddressesFirebase,
  getDefaultShipFromAddress,
} from './shipFromAddressService';
import {
  getPackageDimensions as getPackageDimensionsFirebase,
  getDefaultPackageDimensions,
} from './packageDimensionsService';
import {
  getMiscItems as getMiscItemsFirebase,
  getActiveMiscItems,
} from './miscItemsService';
import { updateOrder } from './orderService';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';

export interface EasyPostValidationResponse {
  isValid: boolean;
  missingFields: string[];
}

export interface EasyPostRateResponse {
  id: string;
  rate: string;
  currency: string;
  carrier: string;
  service: string;
  delivery_date?: string;
  delivery_days?: number;
}

export interface EasyPostLabelResponse {
  labelId: string;
  labelUrl: string;
  trackingCode: string;
  trackingUrl?: string;
  carrier: string;
  service: string;
  rate: number;
  currency: string;
  estimatedDeliveryDate?: string;
  postageLabel: {
    id: string;
    created_at: string;
    updated_at: string;
    label_url: string;
    label_pdf_url?: string;
  };
  shipment: {
    id: string;
    created_at: string;
    updated_at: string;
    carrier: string;
    service: string;
    estimated_delivery_date?: string;
  };
  customs?: {
    id: string;
    contents_type: string;
    customs_certify: boolean;
    customs_signer: string;
  };
}

/**
 * Get ship-from addresses for EasyPost (using Firebase directly)
 */
export async function getShipFromAddresses(): Promise<ShipFromAddress[]> {
  try {
    return await getShipFromAddressesFirebase();
  } catch (error) {
    console.error('Error fetching ship-from addresses:', error);
    throw error;
  }
}

/**
 * Get package dimensions for EasyPost (using Firebase directly)
 */
export async function getPackageDimensions(): Promise<PackageDimensions[]> {
  try {
    return await getPackageDimensionsFirebase();
  } catch (error) {
    console.error('Error fetching package dimensions:', error);
    throw error;
  }
}

/**
 * Get misc items for EasyPost (using Firebase directly)
 */
export async function getMiscItems(): Promise<MiscItem[]> {
  try {
    return await getActiveMiscItems();
  } catch (error) {
    console.error('Error fetching misc items:', error);
    throw error;
  }
}

/**
 * Get default shipping configuration
 */
export async function getDefaultShippingConfig(): Promise<{
  shipFromAddress: ShipFromAddress | null;
  packageDimensions: PackageDimensions | null;
}> {
  try {
    const [shipFromAddress, packageDimensions] = await Promise.all([
      getDefaultShipFromAddress(),
      getDefaultPackageDimensions(),
    ]);

    return {
      shipFromAddress,
      packageDimensions,
    };
  } catch (error) {
    console.error('Error fetching default shipping config:', error);
    throw error;
  }
}

/**
 * Validate customs data for an order
 */
export async function validateCustomsData(
  orderId: string,
  selectedMiscItems: { itemId: string; quantity: number }[] = []
): Promise<EasyPostValidationResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/shipping/validate-customs/${orderId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ selectedMiscItems }),
    });

    if (!response.ok) {
      throw new Error('Failed to validate customs data');
    }

    return await response.json();
  } catch (error) {
    console.error('Error validating customs data:', error);
    throw error;
  }
}

/**
 * Get shipping rates for an order
 */
export async function getShippingRates(
  orderId: string,
  shipFromAddressId: string,
  packageDimensionsId: string,
  selectedMiscItems: { itemId: string; quantity: number }[] = [],
  contentsType: 'merchandise' | 'gift' | 'documents' | 'sample' | 'returned_goods' | 'other' = 'merchandise'
): Promise<EasyPostRateResponse[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/shipping/rates/${orderId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        shipFromAddressId,
        packageDimensionsId,
        selectedMiscItems,
        contentsType,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to get shipping rates');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting shipping rates:', error);
    throw error;
  }
}

/**
 * Generate shipping label
 */
export async function generateShippingLabel(
  orderId: string,
  shipFromAddressId: string,
  packageDimensionsId: string,
  selectedMiscItems: { itemId: string; quantity: number }[] = [],
  contentsType: 'merchandise' | 'gift' | 'documents' | 'sample' | 'returned_goods' | 'other' = 'merchandise'
): Promise<EasyPostLabelResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/shipping/generate-label/${orderId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        shipFromAddressId,
        packageDimensionsId,
        selectedMiscItems,
        contentsType,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to generate shipping label');
    }

    return await response.json();
  } catch (error) {
    console.error('Error generating shipping label:', error);
    throw error;
  }
}

/**
 * Save EasyPost label to order (using Firebase directly)
 */
export async function saveLabelToOrder(
  orderId: string,
  labelData: EasyPostLabelResponse,
  trackingData: {
    carrier: string;
    carrierName: string;
    trackingNumber: string;
    url?: string;
    status: string;
    estimatedDelivery?: Date;
  }
): Promise<{ success: boolean; message: string }> {
  try {
    // Convert EasyPostLabelResponse to EasyPostLabel format for the Order type
    const easyPostLabel: EasyPostLabel = {
      id: labelData.labelId,
      labelUrl: labelData.labelUrl,
      trackingCode: labelData.trackingCode,
      trackingUrl: labelData.trackingUrl,
      postageLabel: labelData.postageLabel,
      shipment: labelData.shipment,
      rate: {
        id: labelData.labelId,
        service: labelData.service,
        carrier: labelData.carrier,
        rate: labelData.rate.toString(),
        currency: labelData.currency,
        delivery_date: labelData.estimatedDeliveryDate,
      },
      customs: labelData.customs ? {
        ...labelData.customs,
        contents_type: labelData.customs.contents_type as 'merchandise' | 'gift' | 'documents' | 'sample' | 'returned_goods' | 'other'
      } : undefined,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Create tracking object
    const tracking = {
      carrier: trackingData.carrier,
      trackingNumber: trackingData.trackingNumber,
      url: trackingData.url,
      status: trackingData.status,
      estimatedDelivery: trackingData.estimatedDelivery,
      lastUpdate: new Date(),
    };

    // Update the order with EasyPost label and tracking information
    await updateOrder(orderId, {
      easyPostLabel,
      tracking,
      updatedAt: new Date(),
    });

    return {
      success: true,
      message: 'EasyPost label saved to order successfully',
    };
  } catch (error) {
    console.error('Error saving label to order:', error);
    throw error;
  }
}